/*
 * 轻量过渡遮罩管理器（无依赖，直接操作 DOM）
 * 用途：在刷新/重置/重连等重操作前淡入遮罩，刷新后在页面加载完成再淡出，降低白屏突兀感。
 */

type OverlayTheme = 'light' | 'dark';

interface ShowOptions {
  theme?: OverlayTheme;
  blocking?: boolean;
}

class OverlayManager {
  private overlayEl: HTMLDivElement | null = null;

  private textEl: HTMLDivElement | null = null;

  private styleEl: HTMLStyleElement | null = null;

  private initialized = false;

  init() {
    if (this.initialized) return;
    this.injectStyle();
    this.ensureOverlay();
    this.initialized = true;
  }

  private injectStyle() {
    if (this.styleEl) return;
    const style = document.createElement('style');
    style.setAttribute('data-overlay-style', '1');
    style.innerHTML = `
      #app-transition-overlay { 
        position: fixed; inset: 0; z-index: 2147483000; 
        display: flex; align-items: center; justify-content: center; 
        background: transparent;
        color: #fff; 
        opacity: 0; pointer-events: none; 
        transition: opacity 220ms ease;
      }
      #app-transition-overlay.visible { opacity: 1; }
      #app-transition-overlay.blocking.visible { pointer-events: auto; }

      /* 主题：暗色（用于离开/刷新等强遮罩） */
      #app-transition-overlay.theme-dark.visible {
        background: rgba(0,0,0,0.52);
        backdrop-filter: blur(2px);
      }
      /* 主题：浅色（用于进入/连接等弱遮罩，减少“黑屏”突兀） */
      #app-transition-overlay.theme-light.visible {
        background: rgba(0,0,0,0.18);
        backdrop-filter: blur(6px) saturate(1.1);
      }
      #app-transition-overlay .spinner { 
        width: 28px; height: 28px; border-radius: 50%; 
        border: 2px solid rgba(255,255,255,0.4); 
        border-top-color: #fff; 
        animation: overlay-spin 0.8s linear infinite; 
        margin-right: 10px;
      }
      #app-transition-overlay .text { font-size: 14px; opacity: 0.92; }
      @keyframes overlay-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      @media (prefers-reduced-motion: reduce) { 
        #app-transition-overlay { transition: none; } 
        #app-transition-overlay .spinner { animation: none; }
      }
    `;
    document.head.appendChild(style);
    this.styleEl = style;
  }

  private ensureOverlay() {
    if (this.overlayEl) return;
    const el = document.createElement('div');
    el.id = 'app-transition-overlay';
    const spinner = document.createElement('div');
    spinner.className = 'spinner';
    const text = document.createElement('div');
    text.className = 'text';
    text.textContent = '请稍候…';
    el.appendChild(spinner);
    el.appendChild(text);
    document.body.appendChild(el);
    this.overlayEl = el;
    this.textEl = text;
  }

  show(text?: string, options?: ShowOptions) {
    this.init();
    if (text && this.textEl) this.textEl.textContent = text;
    const theme: OverlayTheme = options?.theme || 'dark';
    const blocking = options?.blocking !== undefined ? options.blocking : true;
    if (this.overlayEl) {
      this.overlayEl.classList.remove('theme-dark', 'theme-light', 'blocking');
      this.overlayEl.classList.add(`theme-${theme}`);
      if (blocking) this.overlayEl.classList.add('blocking');
      this.overlayEl.classList.add('visible');
    }
  }

  hide() {
    if (!this.initialized) return;
    this.overlayEl?.classList.remove('visible');
  }

  // 先淡入，再执行回调；常用于刷新前/离开前
  async fadeInThen<T>(fn: () => Promise<T> | T, text?: string, options?: ShowOptions): Promise<T> {
    this.show(text, options);
    // 等待一帧确保过渡生效
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
    return Promise.resolve(fn());
  }

  // 页面加载后淡出（与会话之间的进入动效）
  fadeOutOnLoad(options?: ShowOptions, delayMs = 200) {
    this.init();
    // 初始可见，稍后淡出
    this.show(undefined, options);
    window.setTimeout(() => this.hide(), delayMs);
  }
}

export const overlayManager = new OverlayManager();

export default overlayManager;
